import { JWTDecodeParams, JWTEncodeParams } from "next-auth/jwt";
import { JWT } from "next-auth/jwt";
import { isSupportOperatorEmail } from "@/lib/support/operator";
import { toast } from "react-toastify";
import { captureException } from "@sentry/nextjs";
import {
  isTransientOutboundError,
  TRANSIENT_OUTBOUND_FINGERPRINT,
  TRANSIENT_OUTBOUND_TAG_KEY,
} from "@/lib/transientError";

// Lazy-load jsonwebtoken — its transitive dep `jwa` accesses Node.js crypto
// internals at import time, crashing Next.js build-time page data collection.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const getJwtSync = (): typeof import("jsonwebtoken") => require("jsonwebtoken");

const algo = "HS256" as const;
const defaultMaxAge = 30 * 24 * 60 * 60; // 30 days

// Phase 1 of JWT rotation (GON-5158): verifier accepts [primary, secondary] so rotation
// can add JWT_SECRET_KEY_NEXT before flipping the signing key. Signer is untouched —
// only JWT_SECRET_KEY is ever used to sign. Empty-strings are dropped because HS256 verify
// with "" would silently accept tampered tokens.
const jwtSecrets = (): string[] =>
  [process.env.JWT_SECRET_KEY, process.env.JWT_SECRET_KEY_NEXT].filter(
    (s): s is string => !!s
  );

export const jwtEncode = ({
  secret,
  token,
  maxAge = defaultMaxAge,
}: JWTEncodeParams) => {
  const jwtClaims: JWT = {
    ...token,
    token_use: "access",
    iat: Date.now() / 1000,
    exp: Date.now() / 1000 + maxAge,
  };
  return getJwtSync().sign(jwtClaims, secret, { algorithm: algo });
};

// NextAuth calls this with `secret` from its config, but we ignore it and use env-driven
// keys so both primary and secondary are attempted during rotation. Returns null on any
// verification failure (previously threw) so NextAuth treats bad/rotated tokens as "no session"
// rather than 500ing.
export const jwtDecode = ({ token }: JWTDecodeParams) => {
  if (!token) return null;
  const keys = jwtSecrets();
  for (const key of keys) {
    try {
      const claims = getJwtSync().verify(token, key, { algorithms: [algo] });
      // Legacy application tokens have no purpose claim. State-specific claims
      // still reject old AwardWallet tokens signed with either rotation key.
      if (
        typeof claims !== "object" ||
        "return_url" in claims ||
        "nonce" in claims ||
        "aud" in claims ||
        ("token_use" in claims && claims.token_use !== "access")
      ) {
        return null;
      }
      return claims as JWT;
    } catch {
      continue;
    }
  }
  return null;
};

// Internal/public bearer credentials may be sub-only; a browser session may not.
export const jwtSessionDecode = (params: JWTDecodeParams) => {
  const claims = jwtDecode(params);
  if (
    !claims ||
    typeof claims.sub !== "string" ||
    !claims.sub.trim() ||
    typeof claims.email !== "string" ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(claims.email)
  ) {
    return null;
  }
  return claims;
};

export const updateSessionTokenWithCookieUserId = ({
  token,
  jwtSecret,
  userId,
}: {
  token: string;
  jwtSecret: string;
  userId: string;
}): string => {
  const jwt = jwtDecode({ token, secret: jwtSecret });
  // An unverifiable token must not mint a new one — `userId` comes from a
  // client-settable cookie, so spreading `null` into `newJwt` would forge a
  // valid session for whatever user the caller asks for. Passing the invalid
  // token through lets downstream auth reject it.
  if (!jwt) {
    return token;
  }
  if (
    typeof jwt.sub !== "string" ||
    !jwt.sub.trim() ||
    typeof jwt.email !== "string" ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(jwt.email) ||
    !isSupportOperatorEmail(jwt.email)
  ) {
    return token;
  }
  const newJwt = { ...jwt };
  newJwt.sub = userId;
  return jwtEncode({ token: newJwt, secret: jwtSecret });
};

export const getPublicUserToken = async () => {
  const sub = "public_user";
  return {
    token: jwtEncode({
      secret: process.env.JWT_SECRET_KEY!,
      token: { sub },
    }),
    userId: sub,
  };
};

const STATIC_PUBLIC_HOSTS = new Set([
  "localhost:3000",
  "www.gondola.ai",
  "staging.gondola.ai",
]);

// Vercel preview hostnames for this project look like
// `gondola-web-app-git-<branch>-gondola-ai.vercel.app` (branch alias) and
// `gondola-web-<deploy-hash>-gondola-ai.vercel.app` (per-deployment).
// Both end in `-gondola-ai.vercel.app`, so allow that suffix without
// widening to all `*.vercel.app` deployments.
const VERCEL_PREVIEW_SUFFIX = "-gondola-ai.vercel.app";

export const isValidPublicHost = (host: string): boolean => {
  if (STATIC_PUBLIC_HOSTS.has(host)) return true;
  return host.endsWith(VERCEL_PREVIEW_SUFFIX);
};

/**
 * Check if a pathname is an auth page that redirects immediately
 * These pages should be excluded from tracking and API calls to prevent
 * interrupted requests when the page redirects
 */
export const isAuthPage = (pathname: string | null | undefined): boolean => {
  if (!pathname) return false;
  return pathname.startsWith("/auth/mobile");
};

export const hasAdminAccess = (email?: string) => {
  if (email?.split("@")[1] === "gondola.ai") {
    return true;
  }
  return false;
};

type HandleErrorOptions = {
  reportError?: boolean;
  silent?: boolean;
};

type HandleError = (
  error: unknown | Error | string,
  options?: HandleErrorOptions
) => void;

export const handleError: HandleError = (error, options) => {
  // Transient outbound-fetch failures (connect timeouts, socket resets, FetchError
  // wrapper from the generated OpenAPI clients) are upstream-blip class. Suppress
  // the toast but still report so the Sentry alert fires.
  const isTransient = isTransientOutboundError(error);
  const showToast = !options?.silent && !isTransient;

  if (typeof error === "string") {
    showToast && toast.error(error);
    options?.reportError && reportError(new Error(error));
  } else if (error instanceof Error) {
    showToast && toast.error(error.message);
    options?.reportError && reportError(error);
  } else {
    showToast && toast.error("An error occurred");
    options?.reportError && reportError(error);
  }
};

export const reportError = (error: unknown | Error) => {
  if (isTransientOutboundError(error)) {
    captureException(error, {
      tags: { [TRANSIENT_OUTBOUND_TAG_KEY]: "true" },
      fingerprint: TRANSIENT_OUTBOUND_FINGERPRINT,
    });
    return;
  }
  captureException(error);
};

/**
 * Extract a user-facing error message from an API/VGS response body.
 * Used when the backend returns non-200 so we show the real reason instead of a generic message.
 */
export function getErrorMessageFromResponse(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === "string") return data.trim() || null;
  if (typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  const msg =
    (typeof obj.message === "string" && obj.message.trim()) ||
    (typeof obj.error === "string" && obj.error.trim()) ||
    (typeof obj.detail === "string" && obj.detail.trim()) ||
    (typeof obj.details === "string" && obj.details.trim()) ||
    null;
  if (msg) return msg;
  if (Array.isArray(obj.errors) && obj.errors.length > 0) {
    const first = obj.errors[0];
    if (typeof first === "string") return first;
    if (
      first &&
      typeof first === "object" &&
      typeof (first as { message?: string }).message === "string"
    )
      return (first as { message: string }).message;
  }
  return null;
}

export const searchAndBookTestEnv =
  process.env.NEXT_PUBLIC_ENVIRONMENT === "development" ||
  process.env.NEXT_PUBLIC_ENVIRONMENT === "preview";
